import { useSelector } from "react-redux";
import Button from "@/common/elements/Button";
import DataTable from "@/common/elements/DataTable";
import { StyledFlex, StyledSubTableContainer } from "@/common/styles";
import CustomModal from "@/components/CustomModal";
import { useState } from "react";
import { Popconfirm } from "antd";
import StateSubmissionDetails from "./StateSubmissionDetails";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const STATE_SUBMISSIONS_HEADERS = [
  { key: "warrantStateSubmissionId", text: "ID"               },
  { key: "transactionType",          text: "Transaction Type"  },
  { key: "dateSubmitted",            text: "Date Submitted"    },
  { key: "timeSubmitted",            text: "Time Submitted"    },
  { key: "status",                   text: "Status"            },
  { key: "request",                  text: "Request"           },
];

const StateSubmissions = () => {
  const { activeWarrant } = useSelector((s) => s?.warrants ?? {});

  const [submissionsDetailsModal, setSubmissionsDetailsModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md). This tab is only reachable
  // from the "Warrants New" module (single-warrant detail view), so the
  // gridId is hardcoded the same way Bookings' grids are.
  const {
    headers: tableHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig(
    "Warrants New:WarrantsStateSubmissions",
    STATE_SUBMISSIONS_HEADERS
  );

  // warrantsStateSubmissions[] from the active warrant
  const submissions = activeWarrant?.warrantsStateSubmissions ?? [];

  const tableRows = submissions.map((s, i) => ({
    _index:                   i,
    warrantStateSubmissionId: s.warrantStateSubmissionId ?? "",
    transactionType:          s.transactionType          ?? "",
    dateSubmitted:            s.dateSubmitted            ?? "",
    timeSubmitted:            s.timeSubmitted            ?? "",
    status:                   s.status                   ?? "",
    request:                  s.request                  ?? "",
  }));

  const handleRowClick = (data, index) => {
    if (index === undefined || index === null) return;
    setSelectedRowIndex(data?._index ?? index);
  };

  const handleDetails = () => {
    if (selectedRowIndex === null) return;
    setSelectedSubmission(submissions[selectedRowIndex]);
    setSubmissionsDetailsModal(true);
  };

  return (
    <>
      <StyledSubTableContainer>
        <StyledFlex justify="space-between" align="center" marginBottom="10px">
          <h5 style={{ color: "var(--text-blue-dark)", margin: "0" }}>
            State Submissions
          </h5>
          <Button
            onClick={handleDetails}
            disabled={selectedRowIndex === null || isConfiguring}
          >
            Details
          </Button>

          {/* Config — drag-reorder/resize columns */}
          {!isConfiguring ? (
            <Button htmlType="button" onClick={startConfiguring}>
              {configButtonLabel}
            </Button>
          ) : (
            <Popconfirm
              title="Save the current column layout?"
              onConfirm={confirmSave}
              onCancel={discardChanges}
              okText="Yes"
              cancelText="No"
            >
              <Button htmlType="button">{configButtonLabel}</Button>
            </Popconfirm>
          )}
        </StyledFlex>

        <DataTable
          ref={dataTableRef}
          headers={tableHeaders}
          draggable={isConfiguring}
          data={tableRows}
          rowUniqueId="_index"
          onRowClick={handleRowClick}
          onRowDoubleClick={(data) => {
            const idx = data?._index;
            if (idx !== undefined) {
              setSelectedSubmission(submissions[idx]);
              setSubmissionsDetailsModal(true);
            }
          }}
        />
      </StyledSubTableContainer>

      {submissionsDetailsModal && (
        <CustomModal
          show={submissionsDetailsModal}
          header="State Submission Details"
          onHide={() => {
            setSubmissionsDetailsModal(false);
            setSelectedSubmission(null);
          }}
          size="lg"
        >
          <StateSubmissionDetails submission={selectedSubmission} />
        </CustomModal>
      )}
    </>
  );
};

export default StateSubmissions;
